// Centralized UX copy taken directly from 02_UX_COPY_AND_SCREENS.md.
// Kept in one place so tone stays consistent and guardrails are auditable.
// Tone: casual, competitive, warm, slightly mischievous. Never clinical.
// The players provide the disrespect — the app never roasts a named member.

export const COPY = {
  invite: {
    headline: "You've been invited to a crew.",
    description: "A game about how well y'all actually keep up with each other.",
    cta: "JOIN THE CREW",
  },
  notifOnboarding: {
    headline: "Don't miss a drop.",
    body: "Questions drop at a random time on game days, and you only get a few hours to answer. Set this up once and your phone will tell you the moment it happens.",
    ctaAddHome: "ADD TO HOME SCREEN",
    ctaTurnOn: "TURN ON NOTIFICATIONS",
    emailFallback: "No problem. We'll use email as a backup.",
  },
  home: {
    dropLiveCta: "LET'S GET IT",
    lockedIn: "You're locked in.",
    lockedInSub: "Results drop when everyone finishes or time runs out.",
    nothingLive: "Nothing live right now.",
    stayReady: "Stay ready.",
    viewLeaderboard: "VIEW LEADERBOARD",
    checkInDue: "Give the crew the update.",
    checkInCta: "CHECK IN",
    // "You're good for now (7 ready)" left the number dangling with nothing to
    // say what it counted.
    questionsReady: (n: number) =>
      `You've got ${n} question${n === 1 ? "" : "s"} about you ready to play.`,
    questionsReadySub: "They'll show up in drops when it's your turn. Add more any time.",
    recentlyHeading: "Recently",
  },
  checkIn: {
    headline: "Give the crew the update.",
    body: "Talk or type for as long as you want. You don't have to hit everything. These are just places to go.",
    prompts: [
      "What's changed lately?",
      "Anything new with work or career?",
      "What's happening with family or relationships?",
      "Anything you're working toward?",
      "Anything big coming up?",
      "Anything taking up a lot of your time or headspace?",
      "Anything fun, random, or surprising the crew probably doesn't know?",
    ],
    footer: "Share whatever feels worth sharing.",
    startTalking: "START TALKING",
    typeInstead: "TYPE INSTEAD",
    workingOnIt: "Working on it…",
    reviewTranscript: "Review transcript",
    genFailed: "Couldn't build questions from that yet. Try again.",
  },
  candidates: {
    intro: "Here's what we could put in play.",
    labelQuestion: "QUESTION",
    labelOptions: "OPTIONS",
    labelCorrect: "CORRECT ANSWER",
    labelReveal: "WHEN EVERYBODY FINDS OUT",
    playIt: "PLAY IT",
    reframe: "REFRAME",
    edit: "EDIT",
    dontUse: "DON'T USE",
    locked: "Locked.",
    readyCount: (n: number) => `You've got ${n} question${n === 1 ? "" : "s"} ready to play.`,
  },
  drop: {
    // dynamic drop-open notification title
    dropTitle: (n: number) => {
      const word = n === 1 ? "One" : n === 2 ? "Two" : n === 3 ? "Three" : String(n);
      return `👀 ${word} question${n === 1 ? "" : "s"} just dropped.`;
    },
    // Both of these follow commissioner settings — never hardcode the numbers,
    // or the app contradicts the season config the commissioner actually chose.
    // Minutes, because a play window can now be as short as five of them and
    // "You've got 0 hours" is worse than useless.
    pushBody: (minutes: number) => `You've got ${describeWindow(minutes)}.`,
    landingReadiness: (seconds: number) => `Hit START and the clock runs. ${seconds} seconds per question, one at a time.`,
    start: "START",
    letsGetIt: "LET'S GET IT",
    countLabel: (n: number) => `${n} question${n === 1 ? "" : "s"}`,
    aboutAMinute: "About a minute",
  },
  question: {
    progress: (i: number, total: number) => `${i} of ${total}`,
    locked: "Locked.",
    time: "Time.",
    subjectHeadline: "This one's about you. 😏",
    subjectSub: "Sit this one out. We'll see who knows their person.",
    subjectCta: "NEXT",
  },
  waiting: {
    lockedIn: "You're locked in.",
    resultsWhen: "Results drop when everyone finishes or time runs out.",
    doneCount: (done: number, total: number) => `${done} of ${total} players finished.`,
  },
  reveal: {
    // The deck's own words. "Next" is both directions, because either way you
    // are moving on to the next person.
    next: "Next",
    aboutLabel: "A REVEAL ABOUT",
    pushTitle: "👀 Results are in.",
    pushBody: "See what everybody knew.",
    labelQuestion: "QUESTION",
    labelAnswer: "THE ANSWER",
    labelResult: "RESULT",
    labelContext: "CONTEXT",
    aboutYou: "ABOUT YOU",
    resultLine: (got: number, eligible: number) => `${got} of ${eligible} got this one.`,
    leaderboardUpdated: "LEADERBOARD UPDATED",
    seeStandings: "SEE STANDINGS",
  },
  leaderboard: {
    scoring: "1 point per correct answer. No speed bonus.",
  },
} as const;

// Aggregate-only reveal reactions. These react to the *count*, never to a named
// person. Never say "nobody knows X" or interpret a relationship.
export function revealReaction(got: number, eligible: number): string {
  if (eligible <= 0) return "";
  if (got === eligible) return "Everybody knew this one. 👀";
  if (got === 0) return `Nobody had this one. 😂`;
  if (got * 2 === eligible) return "Half the crew had it.";
  if (got === 1) return `Sheesh. 1 of ${eligible} got this one. 😂`;
  return `${got} of ${eligible} had it.`;
}

// Say which crew a notification is from.
//
// A ping is read on a lock screen, out of context, and once someone is in
// several crews "Two questions just dropped" does not say whose. The crew name
// leads the body rather than the title so the hook in the title survives intact
// and both lines still fit.
export function pushBodyForCrew(crewName: string | null | undefined, body: string): string {
  const name = (crewName ?? "").trim();
  return name ? `${name}. ${body}` : body;
}

// How long you have to play, said the way a person would say it.
export function describeWindow(minutes: number): string {
  const m = Math.max(1, Math.round(minutes));
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"}`;
  const hours = m / 60;
  // Whole hours read as hours. Anything else keeps the remainder, because
  // "1 hour" for 95 minutes quietly cheats someone out of half an hour.
  if (Number.isInteger(hours)) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const wholeHours = Math.floor(hours);
  const rem = m - wholeHours * 60;
  if (wholeHours === 0) return `${rem} minutes`;
  return `${wholeHours} hour${wholeHours === 1 ? "" : "s"} ${rem} min`;
}
